import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API, getSessionToken } from "../lib/api";
import { useBilling } from "../lib/billing";
import { SocialInputFallback } from "../components/YoutubeInputFallback";
import {
  Link as LinkIcon, Sparkle, WarningCircle, InstagramLogo, TiktokLogo, YoutubeLogo,
  CrownSimple, UploadSimple, FileVideo, X,
} from "@phosphor-icons/react";

const PROCESS_MESSAGES = [
  "Descargando el vídeo…",
  "Escuchando con atención…",
  "Transcribiendo el audio…",
  "Descartando bromas y chistes…",
  "Pesando los ingredientes…",
  "Numerando los pasos…",
  "Anotando los trucos del chef…",
];

const YOUTUBE_PROCESS_MESSAGES = [
  "Consultando YouTube…",
  "Leyendo la descripción…",
  "Comprobando ingredientes y cantidades…",
  "Ordenando el procedimiento…",
  "Preparando tu receta…",
];

const INSTAGRAM_PROCESS_MESSAGES = [
  "Consultando la información pública…",
  "Leyendo el texto disponible…",
  "Comprobando ingredientes y cantidades…",
  "Buscando un procedimiento fiable…",
  "Preparando tu receta…",
];

const isYouTubeUrl = (value) => /(?:youtube\.com|youtu\.be)/i.test(value || "");
const isInstagramUrl = (value) => /instagram\.com/i.test(value || "");
const emptyManualIngredients = () => [
  { name: "", quantity: "", notes: "" },
  { name: "", quantity: "", notes: "" },
];
const normalizeManualIngredients = (ingredients) => ingredients
  .map((item) => ({
    name: item.name.trim(),
    quantity: item.quantity.trim(),
    notes: item.notes?.trim() || null,
  }))
  .filter((item) => item.name || item.quantity);

export default function Extract() {
  const [mode, setMode] = useState("url"); // 'url' | 'file'
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [sourceUrl, setSourceUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [paywall, setPaywall] = useState(false);
  const [msgIdx, setMsgIdx] = useState(0);
  const [socialNeedsInput, setSocialNeedsInput] = useState(null);
  const [socialTranscript, setSocialTranscript] = useState("");
  const [manualIngredients, setManualIngredients] = useState(emptyManualIngredients);
  const [manualProcedure, setManualProcedure] = useState("");
  const autoShareHandled = useRef(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { status, refresh: refreshBilling } = useBilling();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    const incoming = searchParams.get("url");
    const shouldAutoProcess = searchParams.get("auto") === "1";
    if (incoming) {
      setUrl(incoming);
      setMode("url");
      setManualIngredients(emptyManualIngredients());
      setManualProcedure("");
      setSocialNeedsInput(null);
      setSearchParams({}, { replace: true });
      if (shouldAutoProcess && !autoShareHandled.current) {
        autoShareHandled.current = true;
        window.setTimeout(() => runUrlExtraction(incoming), 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processMessages = isYouTubeUrl(url)
    ? YOUTUBE_PROCESS_MESSAGES
    : isInstagramUrl(url)
      ? INSTAGRAM_PROCESS_MESSAGES
      : PROCESS_MESSAGES;

  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % processMessages.length), 2200);
    return () => clearInterval(t);
  }, [busy, processMessages.length]);

  const limitReached = status && !status.is_pro && status.extraction_count >= status.free_limit;

  const runUrlExtraction = async (targetUrl) => {
    const isSocialRetry = Boolean(socialNeedsInput);
    const normalizedIngredients = normalizeManualIngredients(manualIngredients);
    setError("");
    setPaywall(false);
    setSocialNeedsInput(null);
    if (limitReached) { setPaywall(true); return; }
    if (!targetUrl?.trim()) return;
    setBusy(true); setMsgIdx(0);
    try {
      const { data } = await api.post("/recipes/extract", {
        url: targetUrl.trim(),
        category_id: categoryId || null,
        caption: caption.trim() || null,
        transcript: socialTranscript.trim() || null,
        manual_ingredients: isSocialRetry ? normalizedIngredients : null,
        manual_procedure: isSocialRetry ? manualProcedure.trim() || null : null,
        resume_id: isSocialRetry ? socialNeedsInput?.resume_id || null : null,
      });
      refreshBilling();
      navigate(`/app/recipe/${data.id}`, { replace: true });
    } catch (err) {
      handleError(err);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setPaywall(false);
    if (limitReached) { setPaywall(true); return; }

    if (mode === "url") {
      await runUrlExtraction(url);
    } else {
      if (!file) { setError("Selecciona un archivo de vídeo"); return; }
      setBusy(true); setMsgIdx(1); // skip "downloading" since uploading
      try {
        const normalizedManualIngredients = normalizeManualIngredients(manualIngredients);
        const form = new FormData();
        form.append("file", file);
        if (categoryId) form.append("category_id", categoryId);
        if (caption.trim()) form.append("caption", caption.trim());
        if (sourceUrl.trim()) form.append("source_url", sourceUrl.trim());
        if (normalizedManualIngredients.length) form.append("manual_ingredients_json", JSON.stringify(normalizedManualIngredients));
        if (manualProcedure.trim()) form.append("manual_procedure", manualProcedure.trim());
        const token = getSessionToken();
        const res = await fetch(`${API}/recipes/extract-file`, {
          method: "POST",
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: form,
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw { response: { status: res.status, data: j } };
        }
        const data = await res.json();
        refreshBilling();
        navigate(`/app/recipe/${data.id}`, { replace: true });
      } catch (err) {
        handleError(err);
      }
    }
  };

  const handleError = (err) => {
    const detail = err.response?.data?.detail;
    const socialInputCodes = [
      "youtube_more_info_required",
      "youtube_ingredients_required",
      "youtube_ingredients_invalid",
      "youtube_procedure_required",
      "instagram_more_info_required",
      "instagram_ingredients_required",
      "instagram_ingredients_invalid",
      "instagram_procedure_required",
    ];
    if (err.response?.status === 422 && socialInputCodes.includes(detail?.code)) {
      setSocialNeedsInput(detail);
      if (detail.accepted_ingredients?.length) {
        setManualIngredients((current) => current.some((item) => item.name || item.quantity || item.notes)
          ? current
          : detail.accepted_ingredients.map((item) => ({ ...item, notes: item.notes || "" })));
      }
      const acceptedProcedure = detail.accepted_procedure || detail.pipeline_state?.accepted_procedure;
      if (acceptedProcedure) setManualProcedure((current) => current || acceptedProcedure);
      setError("");
    } else if (err.response?.status === 402) {
      setPaywall(true); refreshBilling();
    } else if (
      err.response?.status === 400 && err.response?.data?.detail?.includes("Threads")
    ) {
      setError(detail);
      // Auto-suggest file mode
      setMode("file");
    } else {
      setError(typeof detail === "string" ? detail : detail?.message || "Algo se nos quemó. Inténtalo de nuevo.");
    }
    setBusy(false);
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="extract-page">
      <p className="eyebrow mb-3">nueva receta</p>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight">
        Pega el enlace y <span className="italic text-[hsl(var(--primary))]">déjanos cocinar</span>.
      </h1>
      <p className="mt-4 text-[hsl(var(--muted-foreground))]">
          Funciona con vídeos públicos de Instagram, TikTok y YouTube. Si falta información, puedes completar el texto o subir un archivo propio/autorizado.
      </p>

      {status && !status.is_pro && (
        <div className="mt-6 rounded-2xl bg-white border border-[hsl(var(--border))] p-4 flex items-center justify-between gap-4" data-testid="usage-banner-extract">
          <div>
            <p className="text-sm font-medium">{status.extraction_count} de {status.free_limit} recetas gratuitas usadas</p>
            <div className="mt-2 h-1.5 w-48 bg-[hsl(var(--muted))] rounded-full overflow-hidden">
              <div className="h-full bg-[hsl(var(--primary))] transition-all duration-500" style={{ width: `${Math.min(100, (status.extraction_count / status.free_limit) * 100)}%` }} />
            </div>
          </div>
          <button onClick={() => navigate("/app/pro")} data-testid="extract-banner-pro-btn" className="shrink-0 inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[hsl(var(--foreground))] text-white text-xs font-semibold btn-press">
            <CrownSimple size={14} weight="fill" /> Hazte Pro
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="mt-10 inline-flex p-1 rounded-full bg-[hsl(var(--muted))]" role="tablist">
        <button
          onClick={() => setMode("url")}
          data-testid="tab-url"
          className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${mode === "url" ? "bg-white shadow-sm text-[hsl(var(--foreground))]" : "text-[hsl(var(--muted-foreground))]"}`}
        >
          <LinkIcon size={14} className="inline -mt-0.5 mr-1" /> Pegar enlace
        </button>
        <button
          onClick={() => setMode("file")}
          data-testid="tab-file"
          className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${mode === "file" ? "bg-white shadow-sm text-[hsl(var(--foreground))]" : "text-[hsl(var(--muted-foreground))]"}`}
        >
          <UploadSimple size={14} className="inline -mt-0.5 mr-1" /> Subir vídeo
        </button>
      </div>

      <form onSubmit={submit} className="mt-6 space-y-5">
        {mode === "file" ? (
          <>
            {normalizeManualIngredients(manualIngredients).length > 0 && (
              <p className="text-sm text-[hsl(var(--secondary))] font-semibold" data-testid="preserved-manual-ingredients-notice">
                Conservamos {normalizeManualIngredients(manualIngredients).length} ingredientes introducidos; el archivo se usará para completar el procedimiento.
              </p>
            )}
            <FileDropzone file={file} onChange={setFile} disabled={busy} />
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">
                Enlace del vídeo original (opcional)
              </label>
              <div className="pill-input flex items-center gap-3">
                <LinkIcon size={20} className="text-[hsl(var(--muted-foreground))] shrink-0" />
                <input
                  data-testid="extract-source-url-input"
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  placeholder="https://www.instagram.com/reel/…"
                  disabled={busy}
                  className="bg-transparent outline-none w-full text-base"
                />
              </div>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5">
                Si pegas el link, podrás volver al vídeo desde la receta.
              </p>
            </div>
          </>
        ) : (
          <div>
            <div className="pill-input flex items-center gap-3">
              <LinkIcon size={20} className="text-[hsl(var(--muted-foreground))] shrink-0" />
              <input
                data-testid="extract-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.instagram.com/reel/…"
                disabled={busy}
                className="bg-transparent outline-none w-full text-base"
              />
            </div>
            <p className="mt-2 px-2 text-xs leading-relaxed text-[hsl(var(--muted-foreground))]" data-testid="extract-incomplete-recipe-note">
              Algunas recetas no incluyen todos los ingredientes, cantidades o pasos. Si falta información, MIZETA te pedirá que la completes manualmente.
            </p>
          </div>
        )}

        {/* Caption textarea shown in BOTH modes — useful when recipe is in pinned comment */}
        {!socialNeedsInput && <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">
            {mode === "url"
              ? "¿La receta está en un comentario fijado? Pégalo aquí (opcional)"
              : "Descripción / caption del vídeo (opcional pero recomendado)"}
          </label>
          <textarea
            data-testid="extract-caption-input"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder={mode === "url"
              ? "Si los ingredientes están en un comentario fijado del autor, cópialos aquí para que MIZETA los use."
              : "Pega el texto que el creador puso debajo del vídeo o en su comentario fijado."}
            disabled={busy}
            rows={4}
            className="w-full bg-white border border-[hsl(var(--border))] rounded-2xl p-4 text-sm focus:outline-none focus:border-[hsl(var(--primary))]"
          />
        </div>}

        {socialNeedsInput && (
          <SocialInputFallback
            info={socialNeedsInput}
            creatorText={caption}
            transcript={socialTranscript}
            ingredients={manualIngredients}
            procedure={manualProcedure}
            busy={busy}
            onCreatorText={setCaption}
            onTranscript={setSocialTranscript}
            onIngredients={setManualIngredients}
            onProcedure={setManualProcedure}
            onRetry={() => runUrlExtraction(url)}
            onUpload={() => {
              setSourceUrl(url);
              setMode("file");
              setSocialNeedsInput(null);
              setError("");
            }}
          />
        )}

        <div className="flex flex-wrap gap-3 items-center">
          <label className="text-sm text-[hsl(var(--muted-foreground))]">Categoría (opcional):</label>
          <select
            data-testid="extract-category-select"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            disabled={busy}
            className="px-4 py-2 rounded-full bg-white border border-[hsl(var(--border))] text-sm focus:outline-none focus:border-[hsl(var(--primary))]"
          >
            <option value="">— Sin categoría —</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {error && (
          <div className="rounded-2xl border border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/5 p-4 flex gap-3" data-testid="extract-error">
            <WarningCircle size={20} className="text-[hsl(var(--destructive))] shrink-0 mt-0.5" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {!socialNeedsInput && (
          <button
            type="submit"
            disabled={busy || (mode === "url" ? !url.trim() : !file)}
            data-testid="extract-submit-btn"
            className="w-full sm:w-auto px-8 py-4 rounded-full bg-[hsl(var(--primary))] text-white font-semibold btn-press hover-lift inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Sparkle size={18} weight="fill" />
            {busy ? "Cocinando…" : (mode === "url" ? "Extraer receta" : "Subir y extraer")}
          </button>
        )}
      </form>

      {busy && (
        <div className="mt-12 rounded-3xl border border-[hsl(var(--border))] bg-white p-10 text-center fade-up" data-testid="extract-processing">
          <div className="flex justify-center gap-2 mb-6">
            <span className="w-3 h-3 rounded-full bg-[hsl(var(--primary))] simmer" />
            <span className="w-3 h-3 rounded-full bg-[hsl(var(--secondary))] simmer-2" />
            <span className="w-3 h-3 rounded-full bg-[hsl(var(--primary))] simmer-3" />
          </div>
          <p className="font-serif italic text-3xl text-[hsl(var(--foreground))]" key={msgIdx}>{processMessages[msgIdx % processMessages.length]}</p>
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">Esto puede tardar entre 30 segundos y 2 minutos según el vídeo.</p>
        </div>
      )}

      <div className="mt-16 flex justify-center gap-8 text-[hsl(var(--muted-foreground))]">
        <InstagramLogo size={28} weight="duotone" />
        <TiktokLogo size={28} weight="duotone" />
        <YoutubeLogo size={28} weight="duotone" />
      </div>

      {paywall && <PaywallModal onClose={() => setPaywall(false)} onUpgrade={() => navigate("/app/pro")} />}
    </div>
  );
}

function FileDropzone({ file, onChange, disabled }) {
  const [drag, setDrag] = useState(false);

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (disabled) return;
    const f = e.dataTransfer.files?.[0];
    if (f) onChange(f);
  };

  if (file) {
    return (
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-white p-5 flex items-center gap-4" data-testid="file-selected">
        <div className="w-12 h-12 rounded-xl bg-[hsl(var(--primary))]/10 flex items-center justify-center shrink-0">
          <FileVideo size={22} className="text-[hsl(var(--primary))]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{file.name}</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">{(file.size / 1024 / 1024).toFixed(1)} MB</p>
        </div>
        <button type="button" onClick={() => onChange(null)} disabled={disabled} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] btn-press">
          <X size={20} />
        </button>
      </div>
    );
  }

  return (
    <label
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      data-testid="file-dropzone"
      className={`block border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-colors ${
        drag ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-[hsl(var(--border))] bg-white hover:border-[hsl(var(--foreground))]/40"
      }`}
    >
      <input
        type="file"
        accept="video/*,audio/*"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        disabled={disabled}
        className="hidden"
        data-testid="file-input"
      />
      <UploadSimple size={32} weight="duotone" className="mx-auto text-[hsl(var(--primary))]" />
      <p className="mt-4 font-serif text-xl">Arrastra un vídeo aquí</p>
      <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">o toca para seleccionarlo · máx. 300 MB</p>
      <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">Útil para Reels de Instagram que requieren login</p>
    </label>
  );
}

function PaywallModal({ onClose, onUpgrade }) {
  return (
    <div className="fixed inset-0 z-50 bg-[hsl(var(--foreground))]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="paywall-modal">
      <div className="relative max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="w-14 h-14 rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center mx-auto mb-5">
          <CrownSimple size={28} weight="fill" className="text-[hsl(var(--primary))]" />
        </div>
        <h3 className="font-serif text-3xl text-center leading-tight">Has llenado tu recetario gratuito</h3>
        <p className="mt-4 text-center text-[hsl(var(--muted-foreground))]">
          Suscríbete a MIZETA Pro para extraer recetas ilimitadas desde Instagram, TikTok y YouTube. Desde <span className="font-semibold text-[hsl(var(--foreground))]">1,99 €/mes</span>.
        </p>
        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="flex-1 px-5 py-3 rounded-full border border-[hsl(var(--border))] btn-press text-sm font-medium">Ahora no</button>
          <button onClick={onUpgrade} data-testid="paywall-upgrade-btn" className="flex-1 px-5 py-3 rounded-full bg-[hsl(var(--primary))] text-white btn-press text-sm font-semibold inline-flex items-center justify-center gap-1">
            <Sparkle size={14} weight="fill" /> Ver planes
          </button>
        </div>
      </div>
    </div>
  );
}
